import type { ResourceKind } from '@/shared/types/resource';

type EditableResourceKind = Extract<ResourceKind, 'document' | 'slide'>;

export interface AiDraftEditorFlushRequest {
  draftId: string;
  resourceId: string;
  resourceKind: EditableResourceKind;
}

export interface AiDraftEditorFlushEventDetail extends AiDraftEditorFlushRequest {
  promises: Array<Promise<boolean>>;
}

export interface AiDraftResourceAppliedDetail {
  draftId: string;
  resourceIds: string[];
  appliedAt: number;
}

const AI_DRAFT_EDITOR_FLUSH_EVENT = 'ai-draft:editor-flush-request';
const AI_DRAFT_RESOURCE_APPLIED_EVENT = 'ai-draft:resource-applied';

export function addAiDraftEditorFlushListener(
  listener: (detail: AiDraftEditorFlushEventDetail) => void
) {
  const handler = (event: Event) => {
    listener((event as CustomEvent<AiDraftEditorFlushEventDetail>).detail);
  };

  window.addEventListener(AI_DRAFT_EDITOR_FLUSH_EVENT, handler);
  return () => window.removeEventListener(AI_DRAFT_EDITOR_FLUSH_EVENT, handler);
}

export async function requestAiDraftEditorFlush(request: AiDraftEditorFlushRequest) {
  const detail: AiDraftEditorFlushEventDetail = {
    ...request,
    promises: [],
  };

  window.dispatchEvent(new CustomEvent(AI_DRAFT_EDITOR_FLUSH_EVENT, { detail }));

  if (detail.promises.length === 0) {
    return true;
  }

  const results = await Promise.allSettled(detail.promises);
  return results.every((result) => result.status === 'fulfilled' && result.value === true);
}

export function addAiDraftResourceAppliedListener(
  listener: (detail: AiDraftResourceAppliedDetail) => void
) {
  const handler = (event: Event) => {
    listener((event as CustomEvent<AiDraftResourceAppliedDetail>).detail);
  };

  window.addEventListener(AI_DRAFT_RESOURCE_APPLIED_EVENT, handler);
  return () => window.removeEventListener(AI_DRAFT_RESOURCE_APPLIED_EVENT, handler);
}

export function notifyAiDraftResourceApplied(detail: AiDraftResourceAppliedDetail) {
  if (detail.resourceIds.length === 0) {
    return;
  }

  window.dispatchEvent(new CustomEvent(AI_DRAFT_RESOURCE_APPLIED_EVENT, { detail }));
}
